import {
  Button,
  CenteredSpinner,
  FormatDate,
  Table,
  Text,
  Title,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import { useManageParameters, useManageVehicleTypeGroups } from '~/common/api';
import { Trans, useI18n, useLocale } from '~/common/context';
import { useManageWcwSearchHistory } from '~/purchase/api';
import { WcwSearchHelpers } from '~/purchase/helpers';
import { IWcwListState } from '~/purchase/types';

type Props = {
  onSearch: (payload: Partial<IWcwListState>) => void;
};
function WcwSearchHistoryList({ onSearch }: Props) {
  const locale = useLocale();
  const manageWcwSearchHistory = useManageWcwSearchHistory({
    list: {
      enabled: true,
    },
  });
  const manageParameters = useManageParameters({
    getSeasons: {
      enabled: true,
    },
  });
  const manageVehicleTypeGroups = useManageVehicleTypeGroups({
    coreVehicleTypeGroup: { enabled: true },
  });
  const { t } = useI18n();
  return (
    <>
      <Title heading='lg' color='secondary-gray' weight='bold' className='mt-4'>
        <Trans i18nKey='app.purchase.search_page.history.title' />
      </Title>
      {(manageWcwSearchHistory.list.isProcessing ||
        manageParameters.getSeasons.isProcessing ||
        manageVehicleTypeGroups.coreVehicleTypeGroup.isProcessing) && (
        <CenteredSpinner />
      )}
      <Table>
        <thead>
          <tr>
            <td></td>
            <td align='center'>
              <Trans i18nKey='app.purchase.search_page.vehicle_type' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.purchase.wcw.search_page.history.selection' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.common.product.season' />
            </td>
            <td align='center'>
              <Trans i18nKey='app.common.quantity' />
            </td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          {manageParameters.getSeasons.isSuccess &&
            manageVehicleTypeGroups.coreVehicleTypeGroup.isSuccess &&
            manageWcwSearchHistory.list.isSuccess &&
            manageWcwSearchHistory.list.data?.data.map((searchEntry) => {
              const vehicleGroup =
                manageVehicleTypeGroups.coreVehicleTypeGroup.data?.data.find(
                  (group) => group.name === searchEntry.query.vehicleGroupCode,
                );
              return (
                <tr key={searchEntry.id}>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '15%' }}
                  >
                    <Text color='secondary-gray' heading='sm'>
                      <FormatDate
                        date={searchEntry.searchedAt}
                        format={DateUtils.DATE_FORMAT.RELATIVE_TIME}
                        locale={locale.locale}
                      />
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '15%' }}
                  >
                    {vehicleGroup ? (
                      <Text color='secondary' heading='sm' weight='semi-bold'>
                        <Trans i18nKey={vehicleGroup?.i18nKey}>
                          {vehicleGroup?.name}
                        </Trans>
                      </Text>
                    ) : (
                      searchEntry.query.vehicleGroupCode
                    )}
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '25%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      {WcwSearchHelpers.formatWcwSelection(searchEntry.query)}
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '15%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      {searchEntry.query.seasonPublicKeys
                        .map((seasonId) => {
                          const seasonItem =
                            manageParameters.getSeasons.data?.data.find(
                              (season) => season.publicKey === seasonId,
                            );
                          return seasonItem?.i18nKey
                            ? t(seasonItem.i18nKey)
                            : seasonItem?.name;
                        })
                        ?.join(', ')}
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '10%' }}
                  >
                    <Text color='secondary' heading='sm' weight='semi-bold'>
                      <Trans
                        i18nKey='app.purchase.wcw.search_page.set_option'
                        values={{
                          count: searchEntry.query.set,
                        }}
                      />
                    </Text>
                  </td>
                  <td
                    className='align-middle'
                    align='center'
                    style={{ width: '20%' }}
                  >
                    <Button
                      id='relaunch'
                      variant='tertiary-blue'
                      onClick={() => {
                        onSearch({
                          searchQuery: {
                            vehicleTypeGroup:
                              searchEntry.query.vehicleGroupCode,
                            identifier: searchEntry.query.reference
                              ? {
                                  value: searchEntry.query.reference,
                                  label: searchEntry.query.reference,
                                }
                              : null,
                            rawIdentifier: searchEntry.query.reference ?? null,
                            brand: searchEntry.query.brand ?? null,
                            model: searchEntry.query.model ?? null,
                            versions: searchEntry.query.versions ?? null,
                            seasons: searchEntry.query.seasonPublicKeys,
                            set: searchEntry.query.set,
                          },
                        });
                      }}
                    >
                      <Trans i18nKey='app.purchase.search_page.history.relaunch_button' />
                    </Button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    </>
  );
}

export default WcwSearchHistoryList;
